import { ContentApiService } from './contentService';
import { Content, ContentSearchParams } from '../../types/content';
import { Topic, TopicSearchParams } from '../../types/topic';
import { ApiResponse, API_STATUS_CODES } from '../../types/api';
import { FetchError } from '../../utils/api';

// Service Layer - Handles data transformation and business logic
export class ContentService {
  private static instance: ContentService;
  private api: ContentApiService;

  private constructor() {
    this.api = ContentApiService.getInstance();
  }

  static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  private validateApiResponse<T>(response: ApiResponse<T>): { results: T[], total: number } {
    if (response.statusCode !== API_STATUS_CODES.SUCCESS || !response.data) {
      // Create a more detailed error that preserves the API response
      const error = new Error('errors.somethingWentWrong') as FetchError;
      error.apiStatusCode = response.statusCode;
      error.apiMessage = response.message;
      error.isApiError = true;
      throw error;
    }
    
    if (Array.isArray(response.data)) {
      return {
        results: response.data,
        total: response.data.length
      };
    }
    
    if (typeof response.data === 'object' && response.data !== null && 'results' in response.data) {
      return {
        results: response.data.results,
        total: response.data.total || 0
      };
    }

    // Handle single item response
    if (typeof response.data === 'object' && response.data !== null) {
      return {
        results: [response.data as T],
        total: 1
      };
    }

    // Create a more detailed error
    const error = new Error('errors.somethingWentWrong') as FetchError;
    error.apiMessage = 'Invalid response format';
    error.isApiError = true;
    throw error;
  }

  async searchContent(params: ContentSearchParams): Promise<{ results: Content[], total: number }> {
    try {
      const { contentType, parent, ...apiParams } = params;
      const queryParams = {
        ...apiParams,
        page: params.page?.toString() || '1',
        limit: params.limit?.toString() || '10',
        ...(parent?.id ? { parent: parent.id } : {})
      };

      const response = await this.api.get<Content>('/content', queryParams);
      return this.validateApiResponse(response);
    } catch (error) {
      // Preserve the original error but ensure it has a user-friendly message
      if (error instanceof Error) {
        // If it's already a properly formatted error, just rethrow it
        if (error.message.startsWith('errors.')) {
          throw error;
        }
        
        // If it's an API error with details, preserve those details
        if ((error as FetchError).isApiError) {
          // The error already has the necessary details from our fetch wrapper
          // Just ensure it has a user-friendly message if needed
          if (!error.message.startsWith('errors.')) {
            error.message = 'errors.somethingWentWrong';
          }
          throw error;
        }
      }
      
      // For other types of errors, wrap them with additional context
      const wrappedError = new Error('errors.somethingWentWrong') as FetchError;
      wrappedError.originalError = error;
      wrappedError.isApiError = true;
      
      // Log the original error for debugging
      console.error('Content search error:', {
        originalError: error,
        params
      });
      
      throw wrappedError;
    }
  }

  async searchTopics(params: TopicSearchParams): Promise<{ results: Topic[], total: number }> {
    try {
      const queryParams = {
        ...params,
        page: params.page?.toString() || '1',
        limit: params.limit?.toString() || '10'
      };

      const response = await this.api.get<Topic>('/topics', queryParams);
      return this.validateApiResponse(response);
    } catch (error) {
      // Preserve the original error but ensure it has a user-friendly message
      if (error instanceof Error) {
        // If it's already a properly formatted error, just rethrow it
        if (error.message.startsWith('errors.')) {
          throw error;
        }
        
        // If it's an API error with details, preserve those details
        if ((error as FetchError).isApiError) {
          // The error already has the necessary details from our fetch wrapper
          // Just ensure it has a user-friendly message if needed
          if (!error.message.startsWith('errors.')) {
            error.message = 'errors.somethingWentWrong';
          }
          throw error;
        }
      }
      
      // For other types of errors, wrap them with additional context
      const wrappedError = new Error('errors.somethingWentWrong') as FetchError;
      wrappedError.originalError = error;
      wrappedError.isApiError = true;
      
      // Log the original error for debugging
      console.error('Topic search error:', {
        originalError: error,
        params
      });
      
      throw wrappedError;
    }
  }

  async getContentById(id: string): Promise<Content> {
    try {
      const response = await this.api.get<Content>(`/content/${id}`);
      if (response.statusCode !== API_STATUS_CODES.SUCCESS || !response.data) {
        // Create a more detailed error that preserves the API response
        const error = new Error('errors.somethingWentWrong') as FetchError;
        error.apiStatusCode = response.statusCode;
        error.apiMessage = response.message;
        error.isApiError = true;
        
        console.log('[ContentService] API Response Error:', {
          statusCode: response.statusCode,
          hasData: !!response.data,
          message: response.message
        });
        
        throw error;
      }
      return 'results' in response.data ? response.data.results[0] : response.data;
    } catch (error) {
      console.log('[ContentService] Caught Error:', {
        errorType: error instanceof Error ? 'Error' : typeof error,
        message: error instanceof Error ? error.message : String(error),
        isTranslationKey: error instanceof Error && error.message.startsWith('errors.'),
        isApiError: (error as FetchError)?.isApiError,
        url: (error as FetchError)?.url,
        status: (error as FetchError)?.status
      });
      
      // Preserve the original error but ensure it has a user-friendly message
      if (error instanceof Error) {
        // If it's already a properly formatted error, just rethrow it
        if (error.message.startsWith('errors.')) {
          throw error;
        }
        
        // If it's an API error with details, preserve those details
        if ((error as FetchError).isApiError) {
          // The error already has the necessary details from our fetch wrapper
          // Just ensure it has a user-friendly message if needed
          if (!error.message.startsWith('errors.')) {
            error.message = 'errors.somethingWentWrong';
          }
          throw error;
        }
      }
      
      // For other types of errors, wrap them with additional context
      const wrappedError = new Error('errors.somethingWentWrong') as FetchError;
      wrappedError.originalError = error;
      wrappedError.isApiError = true;
      
      // Log the original error for debugging
      console.error('Content fetch error:', {
        originalError: error,
        contentId: id
      });
      
      throw wrappedError;
    }
  }

  async getTopicById(id: string): Promise<Topic> {
    try {
      const response = await this.api.get<Topic>(`/topics/${id}`);
      if (response.statusCode !== API_STATUS_CODES.SUCCESS || !response.data) {
        // Create a more detailed error that preserves the API response
        const error = new Error('errors.somethingWentWrong') as FetchError;
        error.apiStatusCode = response.statusCode;
        error.apiMessage = response.message;
        error.isApiError = true;
        throw error;
      }
      return 'results' in response.data ? response.data.results[0] : response.data;
    } catch (error) {
      // Preserve the original error but ensure it has a user-friendly message
      if (error instanceof Error) {
        // If it's already a properly formatted error, just rethrow it
        if (error.message.startsWith('errors.')) {
          throw error;
        }
        
        // If it's an API error with details, preserve those details
        if ((error as FetchError).isApiError) {
          // The error already has the necessary details from our fetch wrapper
          // Just ensure it has a user-friendly message if needed
          if (!error.message.startsWith('errors.')) {
            error.message = 'errors.somethingWentWrong';
          }
          throw error;
        }
      }
      
      // For other types of errors, wrap them with additional context
      const wrappedError = new Error('errors.somethingWentWrong') as FetchError;
      wrappedError.originalError = error;
      wrappedError.isApiError = true;
      
      // Log the original error for debugging
      console.error('Topic fetch error:', {
        originalError: error,
        topicId: id
      });
      
      throw wrappedError;
    }
  }
}